<script lang="ts">
	import { readDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { createEventDispatcher } from 'svelte';
	import { fade } from 'svelte/transition';
	import {
		Button,
		Container,
		Modal,
		Select,
		Text,
		Tabs,
		Tab,
		toast,
	} from '@Shelly/shelly-design-system';
	import type { SelectOptionType } from '@Shelly/shelly-design-system/dist/Select/Select.types';
	import type { Device } from 'src/types/Device';
	import type { OutputSelection } from 'src/types/BTHomeTypes';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { BtHomeController } from 'src/helpers/virtual_helpers/ble_control_helpers';
	import {
		getBthomeDeviceModel,
		bthomeDeviceToModel,
	} from 'src/helpers/virtual_helpers/virtual_bthome_helper';
	import { format } from 'svelte-i18n';
	import { real_id } from 'src/helpers/interface_helpers';

	export let device: Device;
	export let showModal = false;
	export let output: OutputSelection | null = null;
	export let remoteId: string | null = null;
	export let remoteConfig: Record<string, any> = {};
	export let channelLabels: Record<string, string> = {};
	export let existingInputs: Array<Record<string, any>> = [];
	export let remoteExistsInSettings = false;
	export let hasMappedRemotes = false;
	export let pairedDeviceType: string | undefined;

	const MAX_CHANNELS = 4;
	const ZERO_BASED_KEY = 'zeroBased';
	const ONE_BASED_KEY = 'oneBased';

	const channelNumberingOptions = [
		{ title: '0 - 3', value: ZERO_BASED_KEY },
		{ title: '1 - 4', value: ONE_BASED_KEY },
	];

	const zeroBasedChannelOptions = Array.from({ length: MAX_CHANNELS }, (_, idx) => ({
		title: `Channel ${idx}`,
		value: String(idx),
	}));

	const oneBasedChannelOptions = Array.from({ length: MAX_CHANNELS }, (_, idx) => ({
		title: `Channel ${idx + 1}`,
		value: String(idx + 1),
	}));

	/**
	 * Action options for push buttons (58:0, 58:1) - left/right buttons.
	 * Switch output type: on, off, toggle.
	 */
	const switchPushActionOptions = [
		{
			title: $format('label_select', { default: 'Select action' }),
			value: '',
		},
		{ title: $format('option_action_on', { default: 'On' }), value: 'on' },
		{ title: $format('option_action_off', { default: 'Off' }), value: 'off' },
		{ title: $format('option_action_toggle', { default: 'Toggle' }), value: 'toggle' },
	];

	/**
	 * Light output type: on, off, toggle, dim_up, dim_down.
	 */
	const lightPushActionOptions = [
		{
			title: $format('label_select', { default: 'Select action' }),
			value: '',
		},
		{ title: $format('option_action_on', { default: 'On' }), value: 'on' },
		{ title: $format('option_action_off', { default: 'Off' }), value: 'off' },
		{ title: $format('option_action_toggle', { default: 'Toggle' }), value: 'toggle' },
		{ title: $format('option_action_dim_up', { default: 'Dim up' }), value: 'dim_up' },
		{ title: $format('option_action_dim_down', { default: 'Dim down' }), value: 'dim_down' },
	];

	/**
	 * Cover output type: close, open, cycle, step_up, step_down.
	 */
	const coverPushActionOptions = [
		{
			title: $format('label_select', { default: 'Select action' }),
			value: '',
		},
		{ title: $format('option_action_open', { default: 'Open' }), value: 'open' },
		{ title: $format('option_action_close', { default: 'Close' }), value: 'close' },
		{ title: $format('option_action_cycle', { default: 'Cycle' }), value: 'cycle' },
		{ title: $format('option_action_step_up', { default: 'Step up' }), value: 'step_up' },
		{ title: $format('option_action_step_down', { default: 'Step down' }), value: 'step_down' },
	];

	/**
	 * Action options for rotate buttons (60:0) - scroll/rotate actions.
	 * Light output type: dim_up, dim_down.
	 */
	const lightRotateActionOptions = [
		{
			title: $format('label_select', { default: 'Select action' }),
			value: '',
		},
		{ title: $format('option_action_dim_up', { default: 'Dim up' }), value: 'dim_up' },
		{ title: $format('option_action_dim_down', { default: 'Dim down' }), value: 'dim_down' },
	];

	/**
	 * Switch output type: on, off, toggle.
	 */
	const switchRotateActionOptions = [
		{
			title: $format('label_select', { default: 'Select action' }),
			value: '',
		},
		{ title: $format('option_action_on', { default: 'On' }), value: 'on' },
		{ title: $format('option_action_off', { default: 'Off' }), value: 'off' },
		{ title: $format('option_action_toggle', { default: 'Toggle' }), value: 'toggle' },
	];

	/**
	 * Cover output type: step_up, step_down.
	 */
	const coverRotateActionOptions = [
		{ title: $format('label_select', { default: 'Select action' }), value: '' },
		{ title: $format('option_action_step_up', { default: 'Step up' }), value: 'step_up' },
		{ title: $format('option_action_step_down', { default: 'Step down' }), value: 'step_down' },
	];

	/**
	 * Determines the appropriate action options for push buttons based on the output component type.
	 *
	 * @param {string} outputId - The output component ID (e.g., "cover:0", "switch:0", "light:0", "rgbcct:0")
	 * @returns {SelectOptionType[]} Array of push button action options
	 *
	 * @example
	 * getPushActionOptions("cover:0") // Returns coverPushActionOptions (open, close, cycle, step_up, step_down)
	 * getPushActionOptions("switch:0") // Returns switchPushActionOptions (on, off, toggle)
	 * getPushActionOptions("rgbcct:0") // Returns lightPushActionOptions (on, off, toggle, dim_up, dim_down)
	 */
	const getPushActionOptions = (outputId: string): SelectOptionType[] => {
		if (outputId?.startsWith('cover:') || outputId?.startsWith('roller:')) {
			return coverPushActionOptions;
		}
		if (outputId?.startsWith('switch:')) {
			return switchPushActionOptions;
		}
		return lightPushActionOptions;
	};

	/**
	 * Determines the appropriate action options for rotate buttons based on the output component type.
	 *
	 * @param {string} outputId - The output component ID (e.g., "cover:0", "switch:0", "rgbcct:0", "roller:0")
	 * @returns {SelectOptionType[]} Array of rotate button action options
	 *
	 * @example
	 * getRotateActionOptions("cover:0") // Returns coverRotateActionOptions (step_up, step_down)
	 * getRotateActionOptions("switch:0") // Returns switchRotateActionOptions (on, off, toggle)
	 * getRotateActionOptions("rgbcct:0") // Returns lightRotateActionOptions (dim_up, dim_down)
	 */
	const getRotateActionOptions = (outputId: string): SelectOptionType[] => {
		if (outputId?.startsWith('cover:') || outputId?.startsWith('roller:')) {
			return coverRotateActionOptions;
		}
		if (outputId?.startsWith('switch:')) {
			return switchRotateActionOptions;
		}
		return lightRotateActionOptions;
	};

	/**
	 * Returns action options for remote control left/right buttons.
	 * - For cover/roller outputs: only "Open" and "Close" are allowed.
	 * - For other outputs (switch/light): only "On" and "Off" are allowed.
	 */
	const getRemoteControlButtonOptions = (outputId: string): SelectOptionType[] => {
		if (outputId?.startsWith('cover:') || outputId?.startsWith('roller:')) {
			return [
				{
					title: $format('label_select', { default: 'Select action' }),
					value: '',
				},
				{ title: $format('option_action_open', { default: 'Open' }), value: 'open' },
				{ title: $format('option_action_close', { default: 'Close' }), value: 'close' },
			];
		}

		return [
			{
				title: $format('label_select', { default: 'Select action' }),
				value: '',
			},
			{ title: $format('option_action_on', { default: 'On' }), value: 'on' },
			{ title: $format('option_action_off', { default: 'Off' }), value: 'off' },
		];
	};

	let pushActionOptions: SelectOptionType[] = lightPushActionOptions;
	let rotateActionOptions: SelectOptionType[] = lightRotateActionOptions;

	const getOptionValue = (option: SelectOptionType): string => {
		if (option === null || option === undefined) {
			return '';
		}
		if (typeof option === 'string') {
			return option;
		}
		if (option !== null && 'value' in option) {
			const optionValue = option.value as unknown;
			return optionValue !== undefined && optionValue !== null ? String(optionValue) : '';
		}
		return '';
	};

	const clone = <T>(value: T): T => {
		if (value === null || value === undefined) {
			return value;
		}
		// @ts-ignore structuredClone might not be defined in the TS lib used during build time
		if (typeof structuredClone === 'function') {
			try {
				// @ts-ignore structuredClone might not exist at runtime on older browsers
				return structuredClone(value);
			} catch {}
		}
		try {
			return JSON.parse(JSON.stringify(value));
		} catch {
			return value;
		}
	};

	const clampChannelIndex = (index: number) => {
		if (Number.isNaN(index)) {
			return 0;
		}
		return Math.min(Math.max(index, 0), MAX_CHANNELS - 1);
	};

	const dispatch = createEventDispatcher<{
		close: void;
		saved: { remoteId: string; payload: Record<string, any> };
		delete: { remoteId: string };
	}>();

	let selectedOutputId = 'rgbcct:0';
	let channelNumberingKey: 'zeroBased' | 'oneBased' = ZERO_BASED_KEY;
	let channelIndex = 0;
	let channelNumberingOption: SelectOptionType = channelNumberingOptions[0];
	let channelSelectionOption: SelectOptionType = zeroBasedChannelOptions[0];
	let leftAction: SelectOptionType = lightPushActionOptions[1];
	let rightAction: SelectOptionType = lightPushActionOptions[0];
	let scrollUpAction: SelectOptionType = lightRotateActionOptions[0];
	let scrollDownAction: SelectOptionType = lightRotateActionOptions[0];
	let workingRemoteConfig: Record<string, any> = {};
	let previousChannelIndex = 0;
	let isLoadingChannelActions = false;

	let remoteDeviceId = 'bthomedevice:200';
	let activeRemoteId = remoteDeviceId;

	let isSaving = false;
	let initialized = false;
	let activeTabIndex = 0;

	const getChannelOptionByIndex = (
		index: number,
		numberingKey: 'zeroBased' | 'oneBased',
	): SelectOptionType => {
		const value = numberingKey === ONE_BASED_KEY ? String(index + 1) : String(index);
		const options =
			numberingKey === ONE_BASED_KEY ? oneBasedChannelOptions : zeroBasedChannelOptions;
		return options.find((option) => getOptionValue(option) === value) ?? options[0];
	};

	/**
	 * Finds the matching SelectOptionType for a given action value from the provided options array.
	 * Returns "-- Select Action --" if the action is null, undefined, or empty.
	 *
	 * @param {string | null | undefined} action - The action value to find (e.g., "on", "open", "dim_up")
	 * @param {SelectOptionType[]} options - The array of action options to search in
	 * @param {SelectOptionType} defaultOption - Optional default option to return if not found
	 * @returns {SelectOptionType} The matching option or "-- Select Action --" if action is empty/null
	 *
	 * @example
	 * getActionOption("on", lightPushActionOptions) // Returns the "On" option
	 * getActionOption(null, lightPushActionOptions) // Returns options[0] ("-- Select Action --")
	 * getActionOption("open", coverPushActionOptions) // Returns the "Open" option for cover devices
	 */
	const getActionOption = (
		action: string | null | undefined,
		options: SelectOptionType[],
		defaultOption?: SelectOptionType,
	): SelectOptionType => {
		if (!action || action === '') {
			return defaultOption ?? options[0];
		}
		return (
			options.find((option) => getOptionValue(option) === action) ?? defaultOption ?? options[0]
		);
	};

	const EVENT_KEY_REGEX = /^\d+:\d+$/;

	/**
	 * Extracts an action value from the remote configuration for a specific channel and event.
	 *
	 * @param {Record<string, any>} config - The remote configuration object
	 * @param {string} channelKey - The channel key (e.g., "58:0", "60:0", "58:1")
	 * @param {string} eventKey - The event key (e.g., "single_push@0", "rotate_left@1")
	 * @returns {string | null} The action value if found, null otherwise
	 *
	 * @example
	 * getActionFromConfig(config, "58:0", "single_push@0") // Returns "on" or null
	 * getActionFromConfig(config, "60:0", "rotate_left@2") // Returns "step_up" or null
	 */
	const getActionFromConfig = (
		config: Record<string, any>,
		channelKey: string,
		eventKey: string,
	): string | null => {
		if (!config) {
			return null;
		}

		const eventsSource = config.events ?? config;

		let channelConfig: Record<string, any> | undefined =
			eventsSource[channelKey] && eventsSource[channelKey] !== null
				? (eventsSource[channelKey] as Record<string, any>)
				: undefined;

		if (!channelConfig && EVENT_KEY_REGEX.test(channelKey)) {
			const [objIdStr, idxStr] = channelKey.split(':');
			const objBucket = eventsSource[objIdStr] || eventsSource[Number(objIdStr)];
			if (objBucket) {
				channelConfig = objBucket[idxStr] || objBucket[Number(idxStr)];
			}
		}

		if (!channelConfig || channelConfig === null) {
			return null;
		}

		const eventConfig = channelConfig[eventKey];
		if (!eventConfig || eventConfig === null) {
			return null;
		}

		return typeof eventConfig.action === 'string' ? eventConfig.action : null;
	};

	/**
	 * Fallback helper that tries to find a legacy, unindexed action for a given
	 * event key within a channel (e.g. matches only "single_push", not "single_push@2").
	 * Used for backwards compatibility with older configs that did not use @index suffixes.
	 */
	const getFirstActionWithPrefix = (
		config: Record<string, any>,
		channelKey: string,
		eventPrefix: string,
	): string | null => {
		if (!config) {
			return null;
		}

		const eventsSource = config.events ?? config;
		const channelConfig = eventsSource[channelKey] as Record<string, any> | undefined;
		if (!channelConfig || channelConfig === null) {
			return null;
		}

		const matchingKey = Object.keys(channelConfig).find((key) => key === eventPrefix);
		if (!matchingKey) {
			return null;
		}

		const eventCfg = channelConfig[matchingKey] as Record<string, any> | undefined;
		return eventCfg && typeof eventCfg.action === 'string' ? eventCfg.action : null;
	};

	const sanitizeRemoteConfig = (config: Record<string, any>) => {
		const sanitized: Record<string, any> = {};

		if (config && Object.keys(config).length > 0) {
			Object.entries(config).forEach(([key, value]) => {
				if (!EVENT_KEY_REGEX.test(key)) {
					return;
				}
				sanitized[key] = value && value !== null ? clone(value) : {};
			});

			if (config.addr !== undefined) {
				sanitized.addr = config.addr;
			}

			if (config._attrs && config._attrs !== null) {
				sanitized._attrs = clone(config._attrs);
			}
		}
		return sanitized;
	};

	/**
	 * Sets or removes an action mapping in the remote configuration.
	 * If the action is empty or "-- Select Action --" is selected, the mapping is removed.
	 *
	 * @param {Record<string, any>} config - The remote configuration object to modify (mutated)
	 * @param {string} channelKey - The channel key (e.g., "58:0", "60:0", "58:1")
	 * @param {string} eventKey - The event key (e.g., "single_push@0", "rotate_left@1")
	 * @param {SelectOptionType} option - The selected action option
	 *
	 * @example
	 * setActionOnConfig(config, "58:0", "single_push@0", { title: "On", value: "on" })
	 * // Sets config["58:0"]["single_push@0"] = { action: "on" }
	 *
	 * setActionOnConfig(config, "58:0", "single_push@0", { title: "-- Select Action --", value: "" })
	 * // Removes config["58:0"]["single_push@0"]
	 */
	const setActionOnConfig = (
		config: Record<string, any>,
		channelKey: string,
		eventKey: string,
		option: SelectOptionType,
	) => {
		const action = getOptionValue(option);

		if (!action || action === '') {
			if (config[channelKey] && config[channelKey] !== null) {
				delete config[channelKey][eventKey];
				if (Object.keys(config[channelKey]).length === 0) {
					delete config[channelKey];
				}
			}
			return;
		}

		if (!config[channelKey]) {
			config[channelKey] = {};
		}

		const eventConfig = config[channelKey][eventKey];

		if (eventConfig) {
			config[channelKey][eventKey] = { ...eventConfig, action };
		} else {
			config[channelKey][eventKey] = { action };
		}
	};

	/**
	 * Gets the display label for a channel key, falling back to the provided fallback if not found.
	 *
	 * @param {string} channelKey - The channel key (e.g., "58:0", "60:0")
	 * @param {string} fallback - The fallback label to use if channelLabels doesn't contain the key
	 * @returns {string} The channel label or fallback
	 */
	const getActionLabel = (channelKey: string, fallback: string) => {
		const baseLabel = channelLabels?.[channelKey];
		return baseLabel || fallback;
	};

	/**
	 * Generates a push event key for a channel index and event type.
	 * For single-button devices (idx === 0), returns just the event type without suffix.
	 * For multi-channel devices, returns event type with @index suffix.
	 *
	 * @param {string} eventType - The push event type (single_push, double_push, triple_push, hold_press)
	 * @param {number} idx - The channel index (0-3)
	 * @param {boolean} useSuffix - Whether to always use the @index suffix (default: false, only if idx > 0)
	 * @returns {string} The push event key (e.g., "single_push", "single_push@1", "hold_press@3")
	 *
	 * @example
	 * getPushEventKey('single_push', 0) // Returns "single_push" (single button device)
	 * getPushEventKey('double_push', 1) // Returns "double_push@1" (multi-channel device)
	 * getPushEventKey('hold_press', 0, true) // Returns "hold_press@0" (forced suffix)
	 */
	const getPushEventKey = (eventType: string, idx: number, useSuffix: boolean = false) => {
		if (useSuffix || idx > 0) {
			return `${eventType}@${idx}`;
		}
		return eventType;
	};

	/**
	 * Generates a single_push event key for a channel index.
	 * Channel index is 0-based: 0 = Channel 1, 1 = Channel 2, 2 = Channel 3, 3 = Channel 4
	 *
	 * Always uses the @index suffix (e.g. single_push@0), matching the format
	 * returned by the device (BTHomeControl.List).
	 *
	 * @param {number} idx - The channel index (0-3)
	 * @returns {string} The push event key (e.g., "single_push@0", "single_push@3")
	 *
	 * @example
	 * getSinglePushEventKey(0) // Returns "single_push@0" (Channel 1)
	 * getSinglePushEventKey(3) // Returns "single_push@3" (Channel 4)
	 */
	const getSinglePushEventKey = (idx: number) => getPushEventKey('single_push', idx, true);

	/**
	 * Generates a rotate left event key for a channel index.
	 * Channel index is 0-based: 0 = Channel 1, 1 = Channel 2, 2 = Channel 3, 3 = Channel 4
	 *
	 * @param {number} idx - The channel index (0-3)
	 * @returns {string} The rotate left event key (e.g., "rotate_left@0", "rotate_left@3")
	 *
	 * @example
	 * getRotateLeftEventKey(0) // Returns "rotate_left@0" (Channel 1)
	 * getRotateLeftEventKey(3) // Returns "rotate_left@3" (Channel 4)
	 */
	const getRotateLeftEventKey = (idx: number) => `rotate_left@${idx}`;

	/**
	 * Generates a rotate right event key for a channel index.
	 * Channel index is 0-based: 0 = Channel 1, 1 = Channel 2, 2 = Channel 3, 3 = Channel 4
	 *
	 * @param {number} idx - The channel index (0-3)
	 * @returns {string} The rotate right event key (e.g., "rotate_right@0", "rotate_right@3")
	 *
	 * @example
	 * getRotateRightEventKey(0) // Returns "rotate_right@0" (Channel 1)
	 * getRotateRightEventKey(3) // Returns "rotate_right@3" (Channel 4)
	 */
	const getRotateRightEventKey = (idx: number) => `rotate_right@${idx}`;

	const handleActionChange =
		(setter: (option: SelectOptionType) => void) => (event: CustomEvent<SelectOptionType>) => {
			setter(event.detail);
		};

	/**
	 * Builds a cleaned action configuration from the working remote config.
	 * Preserves all channel mappings (58:0, 58:1, 60:0) and their event keys (single_push@0, rotate_left@1, etc.),
	 * while filtering out invalid or empty actions. Also preserves metadata like 'addr' and '_attrs'.
	 *
	 * @param {Record<string, any>} configToProcess - The working remote configuration to process
	 * @returns {Record<string, any>} Cleaned configuration with only valid action mappings
	 *
	 * @example
	 * buildActionConfig({
	 *   "58:0": { "single_push@0": { action: "on" }, "single_push@1": { action: "off" } },
	 *   "60:0": { "rotate_left@0": { action: "dim_up" } },
	 *   "addr": "28:68:47:ef:9f:4f"
	 * })
	 * // Returns the same structure with all valid actions preserved
	 */
	const buildActionConfig = (configToProcess: Record<string, any>) => {
		if (!configToProcess || !Object.keys(configToProcess).length) {
			return {};
		}
		const config = clone(configToProcess);

		const cleaned: Record<string, any> = {};

		Object.entries(config).forEach(([key, value]) => {
			if (key === 'addr' || key === '_attrs') {
				cleaned[key] = value;
				return;
			}

			if (!value || value === null) {
				return;
			}

			if (EVENT_KEY_REGEX.test(key)) {
				const trimmedEvents: Record<string, any> = {};
				Object.entries(value).forEach(([eventKey, eventCfg]) => {
					const action = (eventCfg as Record<string, any>)?.action;
					if (typeof action === 'string' && action) {
						trimmedEvents[eventKey] = { action };
					}
				});

				if (Object.keys(trimmedEvents).length > 0) {
					cleaned[key] = trimmedEvents;
				}
			}
		});

		if (config.addr !== undefined) {
			cleaned.addr = config.addr;
		}

		if (config._attrs && config._attrs !== null) {
			cleaned._attrs = config._attrs;
		}

		return cleaned;
	};

	/**
	 * Gets the device model to check device type information.
	 * Checks pairedDeviceType first, then falls back to status/settings.
	 */
	const getDeviceModel = () => {
		if (pairedDeviceType) {
			const modelFromType = getModelFromDeviceType(pairedDeviceType);
			if (modelFromType) {
				return modelFromType;
			}
		}

		try {
			const deviceID = real_id(device.id);
			const settingsMap = (readDeviceSettings(deviceID) ?? {}) as Record<string, any>;
			const statusMap = (readDeviceStatus(deviceID) ?? {}) as Record<string, any>;
			const candidateIds = getCandidateRemoteIds();
			const remoteStatus = findRemoteStatus(candidateIds, statusMap, settingsMap);

			if (remoteStatus) {
				return getBthomeDeviceModel(remoteStatus as any);
			}
		} catch (error) {
			console.warn('Error getting device model:', error);
		}

		return null;
	};

	/**
	 * Detects if the configuration is for a remote control device (has left/right buttons and scroll)
	 * or a button device (single button with multiple push event types)
	 *
	 * Checks both the config structure AND the device model type to handle cases where
	 * config might be empty (newly paired devices).
	 */
	const isRemoteControlDevice = (config: Record<string, any>): boolean => {
		const deviceModel = getDeviceModel();
		if (deviceModel && deviceModel.deviceType === 'remote_control') {
			return true;
		}

		if (!config || Object.keys(config).length === 0) {
			return false;
		}

		const hasLeftButton = config['58:0'] && Object.keys(config['58:0']).length > 0;
		const hasRightButton = config['58:1'] && Object.keys(config['58:1']).length > 0;
		const hasScroll = config['60:0'] && Object.keys(config['60:0']).length > 0;
		return (hasLeftButton && hasRightButton) || hasScroll;
	};

	/**
	 * Gets candidate remote IDs to check for device model information.
	 * Includes the activeRemoteId and its bthomecontrol counterpart if applicable.
	 */
	const getCandidateRemoteIds = (): string[] => {
		const candidates = [activeRemoteId];
		if (activeRemoteId.startsWith('bthomedevice:')) {
			const numericId = activeRemoteId.split(':')[1];
			if (numericId) {
				candidates.push(`bthomecontrol:${numericId}`);
			}
		}
		return candidates;
	};

	/**
	 * Finds remote status from status map or settings map for the given candidate IDs.
	 */
	const findRemoteStatus = (
		candidateIds: string[],
		statusMap: Record<string, any>,
		settingsMap: Record<string, any>,
	): Record<string, any> | null => {
		for (const candidateId of candidateIds) {
			const statusCandidate = statusMap?.[candidateId];
			if (statusCandidate) {
				return statusCandidate as Record<string, any>;
			}
		}

		for (const candidateId of candidateIds) {
			const settingsCandidate = settingsMap?.[candidateId] as Record<string, any> | undefined;
			const attrsStatus = settingsCandidate?._attrs;
			if (attrsStatus) {
				return attrsStatus as Record<string, any>;
			}
		}

		for (const candidateId of candidateIds) {
			const settingsEntry = settingsMap?.[candidateId] as Record<string, any> | undefined;
			if (settingsEntry && typeof settingsEntry === 'object') {
				return settingsEntry;
			}
		}

		return null;
	};

	/**
	 * Gets device model from pairedDeviceType if available.
	 * Falls back to looking up by device type string in bthomeDeviceToModel array.
	 */
	const getModelFromDeviceType = (deviceType: string | undefined) => {
		if (!deviceType) {
			return null;
		}
		return bthomeDeviceToModel.find((model) => model.type === deviceType) || null;
	};

	/**
	 * Checks if the device supports multiple buttons/channels and rotate events
	 * Returns true for 4-button devices like Wall Switch 4 (SBBT-004CEU, SBBT-004CUS, SBBT-104CEU, SBBT-104CUS)
	 * Returns false for single-button devices like Button1 (SBBT-002C, SBBT-102C)
	 *
	 * This function checks both settings and status to determine the device model,
	 * as the remote device information may be in either location depending on pairing state.
	 * It also uses pairedDeviceType as a fallback when available.
	 */
	const supportsMultiChannelAndRotate = (): boolean => {
		if (!device || !activeRemoteId) {
			return false;
		}

		try {
			if (pairedDeviceType) {
				const modelFromType = getModelFromDeviceType(pairedDeviceType);
				if (modelFromType) {
					return modelFromType.buttons > 1;
				}
			}

			const deviceID = real_id(device.id);
			const settingsMap = (readDeviceSettings(deviceID) ?? {}) as Record<string, any>;
			const statusMap = (readDeviceStatus(deviceID) ?? {}) as Record<string, any>;
			const candidateIds = getCandidateRemoteIds();
			const remoteStatus = findRemoteStatus(candidateIds, statusMap, settingsMap);

			if (!remoteStatus) {
				return false;
			}

			const remoteModel = getBthomeDeviceModel(remoteStatus as any);
			return remoteModel && remoteModel.buttons > 1;
		} catch (error) {
			console.warn('Error checking device model for multi-channel support:', error);
			return false;
		}
	};

	/**
	 * Loads action values from the working remote config for a specific channel index
	 * and updates the UI selectors for **remote control** devices.
	 *
	 * For a given channel index it reads:
	 * - Left button: 58:0 → single_push@idx
	 * - Right button: 58:1 → single_push@idx
	 * - Scroll up: 60:0 → rotate_left@idx
	 * - Scroll down: 60:0 → rotate_right@idx
	 *
	 * @param {number} idx - Channel index (0‑based: 0 = Channel 1, 1 = Channel 2, etc.)
	 */
	const loadChannelActions = (idx: number) => {
		isLoadingChannelActions = true;

		if (!workingRemoteConfig || typeof workingRemoteConfig !== 'object') {
			workingRemoteConfig = {};
		}

		if (Object.keys(workingRemoteConfig).length === 0) {
			const currentRotateOptions = getRotateActionOptions(selectedOutputId);
			const remoteControlButtonOptions = getRemoteControlButtonOptions(selectedOutputId);

			leftAction = remoteControlButtonOptions[0];
			rightAction = remoteControlButtonOptions[0];
			scrollUpAction = currentRotateOptions[0];
			scrollDownAction = currentRotateOptions[0];
			isLoadingChannelActions = false;
			return;
		}

		const currentRotateOptions = getRotateActionOptions(selectedOutputId);
		const remoteControlButtonOptions = getRemoteControlButtonOptions(selectedOutputId);

		const singlePushEventKey = getSinglePushEventKey(idx);
		const rotateLeftEventKey = getRotateLeftEventKey(idx);
		const rotateRightEventKey = getRotateRightEventKey(idx);

		const leftActionValue = getActionFromConfig(workingRemoteConfig, '58:0', singlePushEventKey);
		const rightActionValue = getActionFromConfig(workingRemoteConfig, '58:1', singlePushEventKey);
		const scrollUpActionValue = getActionFromConfig(workingRemoteConfig, '60:0', rotateLeftEventKey);
		const scrollDownActionValue = getActionFromConfig(
			workingRemoteConfig,
			'60:0',
			rotateRightEventKey,
		);

		const resolvedLeftAction =
			leftActionValue ??
			(idx === 0 ? getFirstActionWithPrefix(workingRemoteConfig, '58:0', 'single_push') : null);
		const resolvedRightAction =
			rightActionValue ??
			(idx === 0 ? getFirstActionWithPrefix(workingRemoteConfig, '58:1', 'single_push') : null);

		leftAction = getActionOption(resolvedLeftAction, remoteControlButtonOptions);
		rightAction = getActionOption(resolvedRightAction, remoteControlButtonOptions);
		scrollUpAction = getActionOption(scrollUpActionValue, currentRotateOptions);
		scrollDownAction = getActionOption(scrollDownActionValue, currentRotateOptions);

		isLoadingChannelActions = false;
	};

	const initializeState = () => {
		selectedOutputId = output?.name ?? output?.id ?? 'rgbcct:0';

		channelIndex = 0;
		activeTabIndex = 0;
		channelNumberingKey = ZERO_BASED_KEY;
		channelNumberingOption = channelNumberingOptions[0];
		channelSelectionOption = getChannelOptionByIndex(channelIndex, channelNumberingKey);

		if (remoteId) {
			remoteDeviceId = remoteId;
		}

		const currentRemoteId = remoteDeviceId || remoteId || 'bthomedevice:200';
		let configToUse = remoteConfig || {};

		if (
			!configToUse ||
			Object.keys(configToUse).length === 0 ||
			Object.keys(configToUse).every((key) => !EVENT_KEY_REGEX.test(key))
		) {
			const remoteEntry = existingInputs?.find((entry) => entry && entry[currentRemoteId]);
			if (remoteEntry && remoteEntry[currentRemoteId]) {
				configToUse = remoteEntry[currentRemoteId];
			}
		}

		workingRemoteConfig = sanitizeRemoteConfig(configToUse);

		if (!workingRemoteConfig || typeof workingRemoteConfig !== 'object') {
			workingRemoteConfig = {};
		}

		loadChannelActions(channelIndex);
		previousChannelIndex = channelIndex;

		initialized = true;
	};

	const handleClose = () => {
		dispatch('close');
	};

	const handleChannelNumberingChange = (event: CustomEvent<SelectOptionType>) => {
		const value = getOptionValue(event.detail);
		channelNumberingKey = value === ONE_BASED_KEY ? ONE_BASED_KEY : ZERO_BASED_KEY;
	};

	const handleChannelSelect = (event: CustomEvent<SelectOptionType>) => {
		const rawValue = getOptionValue(event.detail);
		const numericValue = Number(rawValue);
		if (Number.isNaN(numericValue)) {
			return;
		}
		channelIndex = clampChannelIndex(
			channelNumberingKey === ONE_BASED_KEY ? numericValue - 1 : numericValue,
		);
	};

	const handleTabChange = (index: number) => {
		const newChannelIndex = clampChannelIndex(index);
		activeTabIndex = index;
		channelIndex = newChannelIndex;
		if (initialized) {
			loadChannelActions(newChannelIndex);
			previousChannelIndex = newChannelIndex;
		}
	};

	$: isKnownDevice = remoteId || (existingInputs && existingInputs.length > 0);

	const channelTabs = Array.from({ length: MAX_CHANNELS }, (_, idx) => ({
		id: idx,
		key: `channel-${idx}`,
		text:
			$format('label_remote_channel', {
				default: 'Channel',
			}) + ` ${idx + 1}`,
	}));

	let remoteActionConfig: Record<string, any> = {};
	let configuration: Record<string, any> = {};
	let preservedInputs: Array<Record<string, any>> = [];

	const REMOTE_INPUT_REGEX = /^(bthomedevice|bthomecontrol):2\d{2}$/;

	const sanitizeInputs = (inputs: Array<Record<string, any>>) => {
		if (!Array.isArray(inputs)) {
			return [];
		}
		return inputs
			.map((entry) => {
				if (!entry || typeof entry !== 'object') {
					return null;
				}

				const filteredEntry = Object.entries(entry).reduce<Record<string, any>>(
					(acc, [key, value]) => {
						if (REMOTE_INPUT_REGEX.test(key)) {
							acc[key] = clone(value);
						}
						return acc;
					},
					{},
				);

				return Object.keys(filteredEntry).length > 0 ? filteredEntry : null;
			})
			.filter((entry): entry is Record<string, any> => Boolean(entry));
	};

	/**
	 * Filters out metadata fields from a remote device configuration, keeping only obj_id mappings.
	 * Obj_ids must be in the format "number:number" (e.g., "58:0", "60:0").
	 *
	 * @param {Record<string, any>} config - The remote device configuration
	 * @returns {Record<string, any>} Configuration with only obj_id mappings
	 */
	const filterObjIdMappings = (config: Record<string, any>): Record<string, any> => {
		if (!config || typeof config !== 'object') {
			return {};
		}

		const objIdRegex = /^\d+:\d+$/;
		const filtered: Record<string, any> = {};

		Object.entries(config).forEach(([key, value]) => {
			if (objIdRegex.test(key)) {
				filtered[key] = clone(value);
			}
		});

		return filtered;
	};

	/**
	 * Builds the inputs payload for BTHomeControl.Update/Create RPC calls.
	 * Preserves all existing remote configurations exactly as they are, only replacing
	 * the remote being edited with the updated configuration.
	 * Filters out metadata fields (id, addr, name, key, meta) from preserved entries.
	 *
	 * @param {Array<Record<string, any>>} existing - Array of existing remote input configurations from BTHomeControl.List
	 * @param {string} remoteKey - The remote key being updated (e.g., "bthomedevice:200")
	 * @param {Record<string, any>} updateConfig - The updated configuration for the remote being edited
	 * @returns {Array<Record<string, any>>} Array of input configurations with the updated remote
	 *
	 * @example
	 * buildInputsPayload(
	 *   [{ "bthomedevice:200": {...} }, { "bthomedevice:201": {...} }],
	 *   "bthomedevice:200",
	 *   { "58:0": { "single_push@0": { action: "on" } } }
	 * )
	 * // Returns array with bthomedevice:200 replaced and bthomedevice:201 preserved
	 */
	const buildInputsPayload = (
		existing: Array<Record<string, any>>,
		remoteKey: string,
		updateConfig: Record<string, any>,
	) => {
		const inputs: Array<Record<string, any>> = [];

		for (const entry of existing) {
			if (!entry || typeof entry !== 'object') {
				continue;
			}

			if (remoteKey in entry) {
				continue;
			}

			const preservedEntry: Record<string, any> = {};
			for (const [key, value] of Object.entries(entry)) {
				if (REMOTE_INPUT_REGEX.test(key)) {
					const filteredValue = filterObjIdMappings(value);
					preservedEntry[key] = filteredValue ?? {};
				}
			}

			if (Object.keys(preservedEntry).length > 0) {
				inputs.push(preservedEntry);
			}
		}

		inputs.push({
			[remoteKey]: clone(updateConfig),
		});

		return inputs;
	};

	/**
	 * Persists the current remote‑control configuration to the device.
	 *
	 * Builds the final `inputs` payload by merging the edited remote with any preserved
	 * inputs, then calls either `BTHomeControl.Update` (for existing remotes) or
	 * `BTHomeControl.Create` (for new ones). On success it emits a `saved` event with
	 * the resolved `remoteId` and configuration.
	 */
	/**
	 * Checks if an error indicates that the resource already exists.
	 * Error code -106 means "Resource already exists".
	 */
	const isResourceExistsError = (res: SettingRequest | Error | unknown): boolean => {
		if (!res || typeof res !== 'object') {
			return false;
		}

		const r = res as Record<string, any>;
		const errorCode =
			r?.error?.device_error?.code ||
			r?.response?.error?.device_error?.code ||
			r?.data?.error?.device_error?.code;
		const errorMessage =
			r?.error?.device_error?.message ||
			r?.response?.error?.device_error?.message ||
			r?.data?.error?.device_error?.message ||
			r?.error?.message ||
			r?.response?.error?.message ||
			r?.data?.error?.message ||
			'';

		if (errorCode === -106) {
			return true;
		}

		if (typeof errorMessage === 'string') {
			const lowerMessage = errorMessage.toLowerCase();
			if (
				lowerMessage.includes('already exists') ||
				(lowerMessage.includes("resource '") && lowerMessage.includes("' already exists"))
			) {
				return true;
			}
		}

		return false;
	};

	const handleSave = async () => {
		if (!device || !activeRemoteId) {
			return;
		}

		isSaving = true;

		const shouldUpdateRemote = hasMappedRemotes && remoteExistsInSettings;
		let rpcMethod = shouldUpdateRemote ? 'BTHomeControl.Update' : 'BTHomeControl.Create';

		const finalRemoteActionConfig = buildActionConfig(workingRemoteConfig);
		const updatedInputs = buildInputsPayload(
			preservedInputs,
			activeRemoteId,
			finalRemoteActionConfig,
		);

		const finalConfiguration = {
			output: selectedOutputId,
			inputs: updatedInputs,
		};

		const payload: Record<string, any> = {
			output: finalConfiguration.output,
			inputs: clone(finalConfiguration.inputs),
		};

		try {
			let res: SettingRequest = await gen2Request(device, rpcMethod, payload);

			if (!res?.isok && rpcMethod === 'BTHomeControl.Create' && isResourceExistsError(res)) {
				rpcMethod = 'BTHomeControl.Update';
				try {
					const enumerateRes: SettingRequest =
						await BtHomeController.getInstance().enumerate(device);
					if (enumerateRes?.isok && enumerateRes?.result) {
						const enumerateResult = enumerateRes.result as Record<string, number>;
						const componentId = enumerateResult[selectedOutputId];
						if (componentId !== undefined && Number.isFinite(componentId)) {
							payload.id = componentId;
						} else {
							const firstOutput = Object.keys(enumerateResult)[0];
							if (firstOutput && enumerateResult[firstOutput] !== undefined) {
								payload.id = enumerateResult[firstOutput];
							} else {
								payload.id = 200;
							}
						}
					} else if (enumerateRes?.isok && enumerateRes?.data?.component !== undefined) {
						payload.id = enumerateRes.data.component;
					} else {
						payload.id = 200;
					}
				} catch (error) {
					payload.id = 200;
					console.error('Error enumerating device:', error);
				}

				res = await gen2Request(device, rpcMethod, payload);
			} else if (rpcMethod === 'BTHomeControl.Update') {
				try {
					const enumerateRes: SettingRequest =
						await BtHomeController.getInstance().enumerate(device);
					if (enumerateRes?.isok && enumerateRes?.data?.component !== undefined) {
						payload.id = enumerateRes.data.component;
					} else if (enumerateRes?.isok && enumerateRes?.result) {
						const enumerateResult = enumerateRes.result as Record<string, number>;
						const componentId = enumerateResult[selectedOutputId];
						if (componentId !== undefined && Number.isFinite(componentId)) {
							payload.id = componentId;
						} else {
							const firstOutput = Object.keys(enumerateResult)[0];
							if (firstOutput && enumerateResult[firstOutput] !== undefined) {
								payload.id = enumerateResult[firstOutput];
							} else {
								payload.id = 200;
							}
						}
					} else {
						payload.id = 200;
					}
				} catch (error) {
					payload.id = 200;
					console.error('Error enumerating device:', error);
				}

				if (!res?.isok) {
					res = await gen2Request(device, rpcMethod, payload);
				}
			}

			if (!res?.isok) {
				toast.open({ type: 'danger', title: $format('error_global') });
				return;
			}
		} catch (error) {
			toast.open({ type: 'danger', title: $format('error_global') });
			console.error('Error saving remote:', error);
		} finally {
			dispatch('saved', { remoteId: activeRemoteId, payload: finalConfiguration });
			isSaving = false;
		}
	};

	$: activeRemoteId = remoteId || 'bthomedevice:200';

	$: if (showModal && !initialized) {
		initializeState();
	}

	$: if (!showModal && initialized) {
		initialized = false;
	}

	$: channelNumberingOption =
		channelNumberingKey === ONE_BASED_KEY ? channelNumberingOptions[1] : channelNumberingOptions[0];

	$: channelSelectionOption = getChannelOptionByIndex(channelIndex, channelNumberingKey);

	$: pushActionOptions = getPushActionOptions(selectedOutputId);
	$: rotateActionOptions = getRotateActionOptions(selectedOutputId);

	$: if (initialized && previousChannelIndex !== channelIndex) {
		loadChannelActions(channelIndex);
		previousChannelIndex = channelIndex;
		if (isKnownDevice) {
			activeTabIndex = channelIndex;
		}
	}

	$: if (
		initialized &&
		!isLoadingChannelActions &&
		leftAction &&
		rightAction &&
		scrollUpAction &&
		scrollDownAction
	) {
		const singlePushEventKey = getSinglePushEventKey(channelIndex);
		const rotateLeftEventKey = getRotateLeftEventKey(channelIndex);
		const rotateRightEventKey = getRotateRightEventKey(channelIndex);

		const currentConfig = workingRemoteConfig || {};
		const isRemoteControl = isRemoteControlDevice(currentConfig);

		if (isRemoteControl) {
			const updatedConfig = clone(currentConfig);

			setActionOnConfig(updatedConfig, '58:0', singlePushEventKey, leftAction);
			setActionOnConfig(updatedConfig, '58:1', singlePushEventKey, rightAction);
			setActionOnConfig(updatedConfig, '60:0', rotateLeftEventKey, scrollUpAction);
			setActionOnConfig(updatedConfig, '60:0', rotateRightEventKey, scrollDownAction);

			const currentConfigStr = JSON.stringify(currentConfig);
			const updatedConfigStr = JSON.stringify(updatedConfig);

			if (currentConfigStr !== updatedConfigStr) {
				workingRemoteConfig = updatedConfig;
			}
		} else if (!currentConfig || typeof currentConfig !== 'object') {
			workingRemoteConfig = {};
		}
	}

	$: remoteActionConfig = buildActionConfig(workingRemoteConfig || {});

	$: preservedInputs = sanitizeInputs(existingInputs);

	$: configuration = {
		output: selectedOutputId,
		inputs:
			activeRemoteId && preservedInputs.length
				? buildInputsPayload(preservedInputs, activeRemoteId, remoteActionConfig)
				: [
						{
							[activeRemoteId]: clone(remoteActionConfig),
						},
					],
	};
</script>

<Modal
	{showModal}
	title={$format('modal_title_configure_remote_control', {
		default: 'Configure remote control',
	})}
	on:close={handleClose}
>
	<Container slot="content" class="d-flex flex-direction-column gap-m">
		<div class="d-flex flex-direction-column gap-xs">
			<Text styleType="h2" class="text-bold">
				{$format('label_output_channel', { default: 'Output channel' })}
			</Text>
			<Text styleType="body-small" class="text-secondary">
				{output?.name || configuration.output}
			</Text>
			{#if isKnownDevice && supportsMultiChannelAndRotate()}
				<Tabs location={activeTabIndex} class="mb-s">
					{#each channelTabs as tab, index (tab.id)}
						<Tab
							active={index === activeTabIndex}
							on:click={(event) => {
								event.preventDefault();
								handleTabChange(index);
							}}
						>
							{tab.text}
						</Tab>
					{/each}
				</Tabs>
			{:else if !isKnownDevice}
				<Text styleType="body-small" class="text-secondary">
					{$format('label_channel_numbering', { default: 'Channel numbering' })}
				</Text>
				<Select
					id="channel-select"
					icon={null}
					label={$format('label_remote_channel', { default: 'Channel' })}
					options={channelNumberingKey === ONE_BASED_KEY
						? oneBasedChannelOptions
						: zeroBasedChannelOptions}
					value={channelSelectionOption}
					aria-label="Select channel"
					on:change={handleChannelSelect}
				/>
			{/if}
		</div>

		<div class="d-flex flex-direction-column gap-xs">
			<Text styleType="h2" class="text-bold">
				{$format('label_button_mapping', { default: 'Button mapping' })}
			</Text>
			{#key channelIndex}
				<div in:fade={{ duration: 200 }}>
					<!-- Remote control device: left/right buttons and scroll -->
					<Text styleType="body-small" class="text-secondary mb-s">
						{$format('label_left_button', { default: 'Left button' })}
					</Text>
					<Select
						class="mb-s"
						id="left-action"
						icon={null}
						label={`${getActionLabel('58:0', 'Left button')} action`}
						options={getRemoteControlButtonOptions(selectedOutputId)}
						value={leftAction}
						aria-label={`${getActionLabel('58:0', 'Left button')} action`}
						on:change={handleActionChange((option) => (leftAction = option))}
					/>
					<Text styleType="body-small" class="text-secondary mb-s">
						{$format('label_right_button', { default: 'Right button' })}
					</Text>
					<Select
						class="mb-s"
						id="right-action"
						icon={null}
						label={`${getActionLabel('58:1', 'Right button')} action`}
						options={getRemoteControlButtonOptions(selectedOutputId)}
						value={rightAction}
						aria-label={`${getActionLabel('58:1', 'Right button')} action`}
						on:change={handleActionChange((option) => (rightAction = option))}
					/>
					<Text styleType="body-small" class="text-secondary mb-s">
						{$format('label_scroll_up', { default: 'Scroll up' })}
					</Text>
					<Select
						class="mb-s"
						id="scroll-up-action"
						icon={null}
						label={`${getActionLabel('60:0', 'Scroll')} up action`}
						options={rotateActionOptions}
						value={scrollUpAction}
						aria-label={`${getActionLabel('60:0', 'Scroll')} up action`}
						on:change={handleActionChange((option) => (scrollUpAction = option))}
					/>
					<Text styleType="body-small" class="text-secondary mb-s">
						{$format('label_scroll_down', { default: 'Scroll down' })}
					</Text>
					<Select
						id="scroll-down-action"
						icon={null}
						label={`${getActionLabel('60:0', 'Scroll')} down action`}
						options={rotateActionOptions}
						value={scrollDownAction}
						aria-label={`${getActionLabel('60:0', 'Scroll')} down action`}
						on:change={handleActionChange((option) => (scrollDownAction = option))}
					/>
				</div>
			{/key}
		</div>
	</Container>

	<div slot="footer" class="d-flex justify-content-end gap-s flex-wrap">
		<Button variant="secondary" on:click={handleClose} disabled={isSaving}>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button variant="primary" on:click={handleSave} loading={isSaving}>
			{$format('button_save', { default: 'Save' })}
		</Button>
	</div>
</Modal>
